import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterSidebar from './components/FilterSidebar';
import ProductCard from './components/ProductCard';
import QuickViewModal from './components/QuickViewModal';
import EnquiryModal from './components/EnquiryModal';
import SortDropdown from './components/SortDropdown';

const ProductCollection = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('featured');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
  const [wishlist, setWishlist] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // grid or list

  // Updated product data with new collection as requested
  const products = [
  {
    id: 1,
    name: "Mava Pure Water 250ml",
    category: "Water Collection",
    image: "https://images.unsplash.com/photo-1597685866437-1116da6f4046",
    imageAlt: "Mava 250ml pure water bottle with premium compact design",
    images: [
    "https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=500",
    "https://images.unsplash.com/photo-1724314459505-e9ddc0c135a2"],
    price: 15,
    originalPrice: 20,
    capacity: "250ml",
    material: "100% Recycled Plastic",
    insulation: "Standard",
    warranty: "Quality Guarantee",
    rating: 4.7,
    reviewCount: 189,
    features: ["BPA Free", "Leak Proof", "Pure Water", "Compact Size"],
    description: `Mava Pure Water 250ml - perfect for quick hydration needs. Our smallest bottle delivering premium water quality in a convenient compact size.`,
    sustainabilityScore: 95,
    isNew: false,
    isLimited: false,
    isBestseller: true,
    capacityOptions: ["250ml"]
  },
  {
    id: 2,
    name: "Mava Pure Water 300ml",
    category: "Water Collection",
    image: "https://images.unsplash.com/photo-1689621984208-cee825671858",
    imageAlt: "Mava 300ml pure water bottle with ergonomic design and premium quality",
    images: [
    "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=500",
    "https://images.unsplash.com/photo-1597685866437-1116da6f4046"],
    price: 20,
    originalPrice: 25,
    capacity: "300ml",
    material: "Recycled Plastic",
    insulation: "Standard",
    warranty: "Quality Guarantee",
    rating: 4.8,
    reviewCount: 245,
    features: ["Personal Size", "Pure Water", "Eco-Friendly", "Easy Grip"],
    description: `Mava Pure Water 300ml - ideal for personal hydration. Perfect balance of size and capacity for everyday hydration needs.`,
    sustainabilityScore: 92,
    isNew: true,
    isLimited: false,
    isBestseller: false,
    capacityOptions: ["300ml"]
  },
  {
    id: 3,
    name: "Mava Pure Water 500ml",
    category: "Water Collection",
    image: "https://images.unsplash.com/photo-1597685866437-1116da6f4046",
    imageAlt: "Mava 500ml pure water bottle with premium design on white background",
    images: [
    "https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=500",
    "https://images.unsplash.com/photo-1698846650971-497504a8bb99"],
    price: 25,
    originalPrice: 30,
    capacity: "500ml",
    material: "100% Recycled Plastic",
    insulation: "Standard",
    warranty: "Quality Guarantee",
    rating: 4.8,
    reviewCount: 324,
    features: ["BPA Free", "Leak Proof", "Pure Water", "Standard Size"],
    description: `Mava Pure Water 500ml - perfect for individual hydration needs. Made from 100% recycled plastic bottles, delivering the purest water quality.`,
    sustainabilityScore: 95,
    isNew: false,
    isLimited: false,
    isBestseller: true,
    capacityOptions: ["500ml"]
  },
  {
    id: 4,
    name: "Mava Pure Water 700ml",
    category: "Water Collection",
    image: "https://images.unsplash.com/photo-1597685866437-1116da6f4046",
    imageAlt: "Mava 700ml pure water bottle with larger capacity and ergonomic design",
    images: [
    "https://images.unsplash.com/photo-1613637069941-06b7dbc394a4",
    "https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=500"],
    price: 35,
    originalPrice: 40,
    capacity: "700ml",
    material: "Recycled Plastic",
    insulation: "Standard",
    warranty: "Quality Guarantee",
    rating: 4.9,
    reviewCount: 198,
    features: ["Large Size", "Easy Grip", "Pure Water", "Durable"],
    description: `Mava Pure Water 700ml - perfect for extended hydration needs. Larger capacity for those who need more water throughout the day.`,
    sustainabilityScore: 90,
    isNew: false,
    isLimited: false,
    isBestseller: true,
    capacityOptions: ["700ml"]
  },
  {
    id: 5,
    name: "Mava Pure Water 1 Liter",
    category: "Water Collection",
    image: "https://images.unsplash.com/photo-1597685866437-1116da6f4046",
    imageAlt: "Mava 1 liter pure water bottle with modern design and premium quality",
    images: [
    "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=500",
    "https://images.unsplash.com/photo-1597685866437-1116da6f4046"],
    price: 40,
    originalPrice: 50,
    capacity: "1000ml",
    material: "Recycled Plastic",
    insulation: "Standard",
    warranty: "Quality Guarantee",
    rating: 4.9,
    reviewCount: 156,
    features: ["Family Size", "Pure Water", "Eco-Friendly", "Value Pack"],
    description: `Mava Pure Water 1 Liter - ideal for families and sharing. Our premium water quality in a convenient 1-liter bottle made from recycled materials.`,
    sustainabilityScore: 88,
    isNew: true,
    isLimited: false,
    isBestseller: false,
    capacityOptions: ["1000ml"]
  },
  {
    id: 6,
    name: "Mava Pure Water 2 Liters - Coming Soon",
    category: "Upcoming Releases",
    image: "https://images.unsplash.com/photo-1597685866437-1116da6f4046",
    imageAlt: "Mava 2 liter pure water bottle with family-size capacity coming soon",
    images: [
    "https://images.unsplash.com/photo-1613637069941-06b7dbc394a4",
    "https://images.unsplash.com/photo-1602143407151-7111542de6e8?w=500"],
    price: 70,
    capacity: "2000ml",
    material: "Recycled Plastic",
    insulation: "Standard",
    warranty: "Quality Guarantee",
    rating: 4.7,
    reviewCount: 89,
    features: ["Large Family Size", "Easy Grip", "Pure Water", "Coming Soon"],
    description: `Mava Pure Water 2 Liters - perfect for large families and group activities. Coming soon to deliver exceptional water purity in large-capacity format.`,
    sustainabilityScore: 92,
    isNew: true,
    isLimited: false,
    isBestseller: false,
    capacityOptions: ["2000ml"]
  },
  {
    id: 7,
    name: "Mava Pure Water 5 Liters - Coming Soon",
    category: "Upcoming Releases",
    image: "https://images.unsplash.com/photo-1583653419130-fa26e2cc28c4",
    imageAlt: "Mava 5 liter pure water bottle with bulk capacity coming soon",
    images: [
    "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=500",
    "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=500"],
    price: 150,
    originalPrice: 180,
    capacity: "5000ml",
    material: "Recycled Plastic",
    insulation: "Standard",
    warranty: "Quality Guarantee",
    rating: 5.0,
    reviewCount: 47,
    features: ["Bulk Size", "Home & Office", "Premium Quality", "Coming Soon"],
    description: `Mava Pure Water 5 Liters - ideal for homes and offices. Coming soon to offer our largest water bottle with exceptional purity and value.`,
    sustainabilityScore: 98,
    isNew: true,
    isLimited: false,
    isBestseller: false,
    capacityOptions: ["5000ml"]
  },
  {
    id: 8,
    name: "Mava Jeera Water 300ml - Coming Soon",
    category: "Upcoming Releases",
    image: "https://images.unsplash.com/photo-1595255753096-763a4484a966",
    imageAlt: "Mava Jeera water 300ml bottle with traditional Indian cumin flavor coming soon",
    images: [
    "https://images.pexels.com/photos/4210866/pexels-photo-4210866.jpeg?w=500",
    "https://images.pexels.com/photos/6621460/pexels-photo-6621460.jpeg?w=500"],
    price: 30,
    capacity: "300ml",
    material: "Recycled Plastic",
    insulation: "Chilled",
    warranty: "Quality Guarantee",
    rating: 4.6,
    reviewCount: 89,
    features: ["Traditional Flavor", "Natural Ingredients", "Refreshing", "Coming Soon"],
    description: `Mava Jeera Water 300ml - Traditional Indian cumin-flavored refreshing drink. Coming soon to bring you authentic taste with modern quality standards.`,
    sustainabilityScore: 100,
    isNew: true,
    isLimited: false,
    isBestseller: false,
    capacityOptions: ["300ml"]
  },
  {
    id: 9,
    name: "Mava Jeera Water 500ml - Coming Soon",
    category: "Upcoming Releases",
    image: "https://images.unsplash.com/photo-1679678691010-985ab6b8ff62",
    imageAlt: "Mava Jeera water 500ml bottle with traditional Indian cumin flavor coming soon",
    images: [
    "https://images.pexels.com/photos/4210866/pexels-photo-4210866.jpeg?w=500",
    "https://images.pexels.com/photos/6621460/pexels-photo-6621460.jpeg?w=500"],
    price: 45,
    capacity: "500ml",
    material: "Recycled Plastic",
    insulation: "Chilled",
    warranty: "Quality Guarantee",
    rating: 4.8,
    reviewCount: 95,
    features: ["Traditional Flavor", "Natural Ingredients", "Standard Size", "Coming Soon"],
    description: `Mava Jeera Water 500ml - Traditional Indian cumin-flavored refreshing drink in standard size. Coming soon for authentic taste experience.`,
    sustainabilityScore: 92,
    isNew: true,
    isLimited: false,
    isBestseller: false,
    capacityOptions: ["500ml"]
  },
  {
    id: 10,
    name: "Mava Jeera Water 1 Liter Pack - Coming Soon",
    category: "Upcoming Releases",
    image: "https://images.unsplash.com/photo-1595255753096-763a4484a966",
    imageAlt: "Mava Jeera water 1 liter pack with traditional Indian cumin flavor coming soon",
    images: [
    "https://images.pexels.com/photos/4210866/pexels-photo-4210866.jpeg?w=500",
    "https://images.pexels.com/photos/6621460/pexels-photo-6621460.jpeg?w=500"],
    price: 80,
    capacity: "1000ml",
    material: "Recycled Plastic",
    insulation: "Chilled",
    warranty: "Quality Guarantee",
    rating: 4.9,
    reviewCount: 67,
    features: ["Traditional Flavor", "Family Pack", "Natural Ingredients", "Coming Soon"],
    description: `Mava Jeera Water 1 Liter Pack - Traditional Indian cumin-flavored refreshing drink in family size. Coming soon to bring authentic taste to your family.`,
    sustainabilityScore: 88,
    isNew: true,
    isLimited: false,
    isBestseller: false,
    capacityOptions: ["1000ml"]
  }];


  // Filter and sort products
  const getFilteredAndSortedProducts = () => {
    let filteredProducts = [...products];

    // Apply filters
    Object.keys(filters)?.forEach((filterKey) => {
      const filterValues = filters?.[filterKey];
      if (filterValues && filterValues?.length > 0) {
        if (filterKey === 'priceRange') {
          const range = filterValues;
          if (range === '0-1000') {
            filteredProducts = filteredProducts?.filter((p) => p?.price <= 1000);
          } else if (range === '1000-2500') {
            filteredProducts = filteredProducts?.filter((p) => p?.price > 1000 && p?.price <= 2500);
          } else if (range === '2500-5000') {
            filteredProducts = filteredProducts?.filter((p) => p?.price > 2500 && p?.price <= 5000);
          } else if (range === '5000-10000') {
            filteredProducts = filteredProducts?.filter((p) => p?.price > 5000 && p?.price <= 10000);
          } else if (range === '10000+') {
            filteredProducts = filteredProducts?.filter((p) => p?.price > 10000);
          }
        } else if (filterKey === 'category') {
          filteredProducts = filteredProducts?.filter((p) =>
          filterValues?.some((value) => p?.category?.toLowerCase()?.includes(value?.replace('-', ' ')))
          );
        } else if (filterKey === 'material') {
          filteredProducts = filteredProducts?.filter((p) =>
          filterValues?.some((value) => p?.material?.toLowerCase()?.includes(value?.replace('-', ' ')))
          );
        } else if (filterKey === 'capacity') {
          filteredProducts = filteredProducts?.filter((p) =>
          filterValues?.includes(p?.capacity)
          );
        }
      }
    });

    // Apply sorting
    switch (sortBy) {
      case 'name-asc':
        filteredProducts?.sort((a, b) => a?.name?.localeCompare(b?.name));
        break;
      case 'name-desc':
        filteredProducts?.sort((a, b) => b?.name?.localeCompare(a?.name));
        break;
      case 'price-asc':
        filteredProducts?.sort((a, b) => a?.price - b?.price);
        break;
      case 'price-desc':
        filteredProducts?.sort((a, b) => b?.price - a?.price);
        break;
      case 'rating':
        filteredProducts?.sort((a, b) => (b?.rating || 0) - (a?.rating || 0));
        break;
      case 'newest':
        filteredProducts?.sort((a, b) => (b?.isNew ? 1 : 0) - (a?.isNew ? 1 : 0));
        break;
      default: // featured
        filteredProducts?.sort((a, b) => (b?.isBestseller ? 1 : 0) - (a?.isBestseller ? 1 : 0));
    }

    return filteredProducts;
  };

  let filteredProducts = getFilteredAndSortedProducts();

  const handleQuickView = (product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleEnquire = (product) => {
    setSelectedProduct(product);
    setIsEnquiryOpen(true);
  };

  const handleWishlist = (product) => {
    setWishlist((prev) => {
      const isInWishlist = prev?.some((item) => item?.id === product?.id);
      if (isInWishlist) {
        return prev?.filter((item) => item?.id !== product?.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleEnquirySubmit = (enquiryData) => {
    console.log('Enquiry submitted:', enquiryData);
    // Here you would typically send to Zoho CRM
    navigate('/thank-you-page');
  };

  const clearAllFilters = () => {
    setFilters({});
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.reduce((count, filterArray) => {
      if (Array.isArray(filterArray)) {
        return count + filterArray?.length;
      } else if (filterArray) {
        return count + 1;
      }
      return count;
    }, 0);
  };

  // Add products to wishlist status
  const productsWithWishlist = filteredProducts?.map((product) => ({
    ...product,
    isWishlisted: wishlist?.some((item) => item?.id === product?.id)
  }));

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-primary/5 via-accent/5 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12">

            <h1 className="text-4xl md:text-5xl font-poppins font-bold text-text-primary mb-4">
              Discover Your Perfect
              <span className="text-primary"> Hydration Companion</span>
            </h1>
            <p className="text-xl text-text-secondary font-inter max-w-3xl mx-auto">
              Explore our premium collection of eco-luxury water bottles designed for conscious living. 
              From smart innovation to timeless heritage, find the bottle that matches your lifestyle.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">

            {[
            { icon: "Package", label: "Products", value: "50+" },
            { icon: "Users", label: "Happy Customers", value: "10K+" },
            { icon: "Leaf", label: "Eco-Friendly", value: "100%" },
            { icon: "Award", label: "Quality Rating", value: "4.8★" }]?.
            map((stat, index) =>
            <div key={index} className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <Icon name={stat?.icon} size={24} className="text-primary" />
                </div>
                <div className="text-2xl font-poppins font-bold text-text-primary">{stat?.value}</div>
                <div className="text-sm text-text-secondary font-inter">{stat?.label}</div>
              </div>
            )}
          </motion.div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <div className="lg:w-80 flex-shrink-0">
              <FilterSidebar
                filters={filters}
                onFiltersChange={setFilters}
                onClearAll={clearAllFilters}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)} />

            </div>

            {/* Products Section */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterOpen(true)}
                    className="lg:hidden">

                    <Icon name="Filter" size={16} className="mr-2" />
                    Filters
                    {getActiveFilterCount() > 0 &&
                    <span className="ml-2 px-2 py-1 bg-primary text-white text-xs rounded-full">
                        {getActiveFilterCount()}
                      </span>
                    }
                  </Button>
                  
                  <div className="hidden sm:block">
                    <p className="text-text-secondary font-inter">
                      Showing <span className="font-semibold text-text-primary">{filteredProducts?.length}</span> of{' '}
                      <span className="font-semibold text-text-primary">{products?.length}</span> products
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {/* View Mode Toggle */}
                  <div className="flex items-center border border-border rounded-lg p-1">
                    <Button
                      variant={viewMode === 'grid' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('grid')}
                      className="px-3">

                      <Icon name="Grid3X3" size={16} />
                    </Button>
                    <Button
                      variant={viewMode === 'list' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('list')}
                      className="px-3">

                      <Icon name="List" size={16} />
                    </Button>
                  </div>

                  {/* Sort Dropdown */}
                  <SortDropdown
                    currentSort={sortBy}
                    onSortChange={setSortBy} />

                </div>
              </div>

              {/* Active Filters */}
              {getActiveFilterCount() > 0 &&
              <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="text-sm font-inter text-text-secondary">Active filters:</span>
                  {Object.entries(filters)?.map(([key, values]) => {
                  if (!values || Array.isArray(values) && values?.length === 0) return null;

                  const filterValues = Array.isArray(values) ? values : [values];
                  return filterValues?.map((value, index) =>
                  <span
                    key={`${key}-${index}`}
                    className="inline-flex items-center space-x-2 px-3 py-1 bg-primary/10 text-primary text-sm font-inter rounded-full">

                        <span>{value?.replace('-', ' ')}</span>
                        <button
                      onClick={() => {
                        if (Array.isArray(filters?.[key])) {
                          const newValues = filters?.[key]?.filter((v) => v !== value);
                          setFilters((prev) => ({
                            ...prev,
                            [key]: newValues?.length > 0 ? newValues : undefined
                          }));
                        } else {
                          setFilters((prev) => ({
                            ...prev,
                            [key]: undefined
                          }));
                        }
                      }}
                      className="hover:bg-primary/20 rounded-full p-0.5 brand-transition">

                          <Icon name="X" size={12} />
                        </button>
                      </span>
                  );
                })}
                  <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-text-secondary hover:text-primary">

                    Clear all
                  </Button>
                </div>
              }

              {/* Products Grid */}
              {filteredProducts?.length > 0 ?
              <motion.div
                layout
                className={`grid gap-6 ${
                viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'}`
                }>

                  {productsWithWishlist?.map((product) =>
                <motion.div
                  key={product?.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}>

                      <ProductCard
                    product={product}
                    onEnquire={handleEnquire}
                    onWishlist={handleWishlist}
                    onQuickView={handleQuickView} />

                    </motion.div>
                )}
                </motion.div> :

              <div className="text-center py-16">
                  <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon name="Search" size={32} className="text-text-secondary" />
                  </div>
                  <h3 className="text-xl font-poppins font-semibold text-text-primary mb-2">
                    No products found
                  </h3>
                  <p className="text-text-secondary font-inter mb-6">
                    Try adjusting your filters or search criteria to find what you're looking for.
                  </p>
                  <Button
                  variant="outline"
                  onClick={clearAllFilters}>

                    Clear all filters
                  </Button>
                </div>
              }
            </div>
          </div>
        </div>
      </section>
      {/* Modals */}
      <QuickViewModal
        product={selectedProduct}
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        onEnquire={handleEnquire} />
      <EnquiryModal
        product={selectedProduct}
        isOpen={isEnquiryOpen}
        onClose={() => setIsEnquiryOpen(false)}
        onSubmit={handleEnquirySubmit} />
    </div>);



};

export default ProductCollection;